import { Button, Flex, Group, Switch, Text, Textarea } from '@mantine/core';
import pluralize from 'pluralize';
import { useState } from 'react';
import { useSupportedExtensions } from 'src/data-capture/file-management/allowedFileTypes';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import {
  DOCUMENT_FILE_EXTENSIONS,
  DOCUMENT_MIME_TYPES,
} from 'src/meetings/detail/tabs/meeting-note/types';
import { MeetingNoteStepProps } from '../types';

const AGENDA_INSTRUCTION_MAX_LENGTH = 2000;
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type GeneratedAgenda = {
  id: string;
  fileName?: string;
  createdAt: string;
  file?: { fileName?: string; fileType?: string } | null;
  backgroundProcess?: { status?: string } | null;
};

function isDocxFile(file: UploadedFile | undefined): boolean {
  if (!file) return false;
  if (file.file.fileType === DOCX_MIME_TYPE) return true;
  return file.file.fileName.toLowerCase().endsWith('.docx');
}

function isGeneratedAgendaDocx(agenda: GeneratedAgenda | undefined): boolean {
  if (!agenda) return false;
  if (agenda.file?.fileType === DOCX_MIME_TYPE) return true;
  const name = agenda.fileName ?? agenda.file?.fileName ?? '';
  return name.toLowerCase().endsWith('.docx');
}

export function NoteAgendaStep({ data, setData }: MeetingNoteStepProps) {
  const clientId = data.isFamily ? data.family?.mainFamilyMember.clientId : data.client?.id;
  const { files, isLoading } = useFileUploads({ clientId });
  const { uploadFiles } = useFileUploadActions({ clientId });
  const [isUploading, setIsUploading] = useState(false);
  const documentExtensions = useSupportedExtensions(DOCUMENT_FILE_EXTENSIONS);

  const { meetingAgendas, isLoading: agendasLoading } = useMeetingAgenda({
    clientId,
    includeFamily: true,
  });

  const documentFiles = filterDocumentFiles(files);
  const generatedAgendas = meetingAgendas.filter(
    agenda => !!agenda.file && agenda.backgroundProcess?.status !== 'FAILED',
  );

  const uploadItems = documentFiles.map(file => ({
    id: file.id,
    title: file.file.fileName,
    description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
  }));
  const generatedItems = generatedAgendas.map(agenda => ({
    id: agenda.id,
    title: agenda.fileName ?? agenda.file?.fileName ?? 'Meeting agenda',
    description: `Generated: ${new Date(agenda.createdAt).toLocaleDateString()}`,
  }));
  const items = [...generatedItems, ...uploadItems];

  const generatedAgendaIds = new Set(generatedAgendas.map(agenda => agenda.id));

  const selectedFile = documentFiles.find(f => f.id === data.selectedAgendaFileId);
  const selectedGeneratedAgenda = generatedAgendas.find(a => a.id === data.selectedMeetingAgendaId);
  const selectedIsDocx = selectedGeneratedAgenda
    ? isGeneratedAgendaDocx(selectedGeneratedAgenda)
    : isDocxFile(selectedFile);
  const selectedItemId = data.selectedMeetingAgendaId ?? data.selectedAgendaFileId;
  const instructionValue = data.agendaInstruction ?? '';
  const instructionLength = instructionValue.length;

  const handleSelectionChange = (selectedIds: string[]) => {
    const selectedId = selectedIds[0] || undefined;
    const selectedItem = items.find(i => i.id === selectedId);
    const isGenerated = !!selectedId && generatedAgendaIds.has(selectedId);
    setData(prev => ({
      ...prev,
      selectedAgendaFileId: isGenerated ? undefined : selectedId,
      selectedMeetingAgendaId: isGenerated ? selectedId : undefined,
      selectedAgendaFileName: selectedItem?.title,
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileId = result[0]?.data?.id;
        if (newUploadedFileId) {
          const uploadedFileName = Array.from(fileList)[0]?.name;
          setData(prev => ({
            ...prev,
            selectedAgendaFileId: newUploadedFileId,
            selectedMeetingAgendaId: undefined,
            selectedAgendaFileName: uploadedFileName,
          }));
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the agenda file. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  if (isLoading || agendasLoading) return null;

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title="Select an agenda for the meeting note">
        <Flex direction="column" gap="md">
          <Flex direction="column" gap="xs" mih={0}>
            <Flex justify="space-between" align="center">
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black">
                {items.length} {pluralize('agenda', items.length)} found
              </Text>
              <Button
                component="label"
                variant="subtle"
                size="compact-sm"
                loading={isUploading}
                style={{ cursor: 'pointer' }}
              >
                Upload agenda
                <input
                  type="file"
                  accept={documentExtensions.join(',')}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </Button>
            </Flex>
            <SelectableItemList
              items={items}
              hint="The agenda structure will be used to generate the meeting note sections. Client information will be added as supplementary context."
              selectedItemIds={selectedItemId ? [selectedItemId] : []}
              setSelectedItemIds={handleSelectionChange}
              selectMultiple={false}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a meeting agenda (PDF, Word, or text file) using the button above and it
                  will appear here.
                </Text>
              }
            />
          </Flex>
          {selectedIsDocx && (
            <Textarea
              label={
                <Group justify="space-between" w="100%">
                  <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                    How should we use this agenda? (optional)
                  </Text>
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.8">
                    {instructionLength}/{AGENDA_INSTRUCTION_MAX_LENGTH}
                  </Text>
                </Group>
              }
              placeholder="Fill out the adviser notes column on each table with the outcomes from the meeting"
              value={instructionValue}
              onChange={event => {
                const { value } = event.currentTarget;
                setData(prev => ({ ...prev, agendaInstruction: value || undefined }));
              }}
              minRows={3}
              autosize
              maxLength={AGENDA_INSTRUCTION_MAX_LENGTH}
              description="Preserves the original agenda's formatting in the generated meeting note."
            />
          )}
          <Flex justify="space-between" align="center" px="xs">
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
              Summarise meeting note
            </Text>
            <Switch
              checked={data.summarise}
              onChange={event => {
                const { checked } = event.currentTarget;
                setData(prev => ({ ...prev, summarise: checked }));
              }}
            />
          </Flex>
        </Flex>
      </StepCard>
    </Flex>
  );
}

function filterDocumentFiles(files: UploadedFile[]): UploadedFile[] {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
}
